import { useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, centavos, formatMoney, type Centavos } from '@abastece/shared';
import { dolaresEsperados, efectivoEsperado, type SesionCaja } from '@abastece/core';
import { readSettings } from '../db/reader.js';
import { emitirYVer } from '../lib/emitir.js';
import { atrasHasta, irA } from '../lib/volver.js';
import { Asistente, indicePaso, type Paso } from '../ui/Asistente.js';
import { Campo } from '../ui/Campo.js';
import { enMoneda } from '../ui/ContarBilletes.js';
import { Dato, Veredicto, calculoDelEsperado } from '../ui/CuentasDeCaja.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { SIN_CONTAR, contado, pasoDeConteo, primerConteo, type Contando } from '../ui/PasoDeConteo.js';
import { useCajaDeOtro } from '../ui/RecibirCaja.js';
import { useT } from '../i18n/index.js';

/**
 * «Recibir la caja de Ana»: the drawer somebody left open, taken over, a step at a time.
 *
 * Ending one's shift is how a till changes hands (the owner, 2026-10-03); this is for when somebody
 * left without doing it. Whoever takes over counts the drawer blind, note by note, asked once more
 * when it is off, and what the count finds over or short is the other person's shift. The drawer
 * then goes on, from what was counted, in this person's name.
 */
export function RecibirLaCaja() {
  const t = useT();
  /** An order «Cobrar» sent to the till while it was somebody else's: the till loads it once it is this person's. */
  const { pedido } = useSearch({ from: '/caja/recibir' });
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const deOtro = useCajaDeOtro();

  const [actual, setActual] = useState('explicacion');
  const [cordobas, setCordobas] = useState<Contando>(SIN_CONTAR);
  const [dolares, setDolares] = useState<Contando>(SIN_CONTAR);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The drawer received, whose it was, and what the count found against it. */
  const [recibida, setRecibida] = useState<{
    caja: SesionCaja;
    nombre: string;
    esperadoC: Centavos;
    esperadoD: Centavos;
    contadoC: Centavos;
    contadoD: Centavos;
    primeroC: Centavos | null;
    primeroD: Centavos | null;
  } | null>(null);
  /**
   * The drawer being received, from the tap: once received it is this person's, and no longer
   * somebody else's to show, a moment before what the count found is drawn.
   */
  const [recibiendo, setRecibiendo] = useState<{ caja: SesionCaja; nombre: string } | null>(null);
  /** Where the step on screen was, for when it goes (`indicePaso`). */
  const ultimo = useRef(0);

  const caja = recibida?.caja ?? recibiendo?.caja ?? deOtro?.caja ?? null;
  const nombre = recibida?.nombre ?? recibiendo?.nombre ?? deOtro?.nombre ?? '';
  const titulo = t('entrega.titulo', { nombre });

  if (deOtro === undefined && !caja) return <Pagina titulo={t('entrega.recibir')} volverA="/caja">{null}</Pagina>;
  if (!caja) {
    return (
      <Pagina titulo={t('entrega.recibir')} volverA="/caja">
        <Vacio mensaje={t('turno.nadaQueRecibir')} icono="caja">
          <Link to="/caja" className="btn-primario px-5">
            <Ico icono="caja" />
            {t('turno.verCaja')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }

  const esperadoC = efectivoEsperado(caja);
  const esperadoD = dolaresEsperados(caja);
  const cC = contado(cordobas, 'NIO');
  const cD = contado(dolares, 'USD');

  async function recibir() {
    if (!caja || cC === null || cD === null) return;
    setGuardando(true);
    setRecibiendo({ caja, nombre });
    setError(null);
    const [primeroC, primeroD] = [primerConteo(cordobas, cC), primerConteo(dolares, cD)];
    try {
      const { rechazo } = await emitirYVer('caja.entregada', {
        sesionId: caja.id,
        conteoCordobas: cC,
        conteoDolares: cD,
        ...(nota.trim() ? { nota: nota.trim() } : {}),
        ...(primeroC !== null ? { primerConteoCordobas: primeroC } : {}),
        ...(primeroD !== null ? { primerConteoDolares: primeroD } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      // Measured against the drawer as it stood before the count: once received, it expects the count.
      setRecibida({ caja, nombre, esperadoC, esperadoD, contadoC: cC, contadoD: cD, primeroC, primeroD });
      setActual('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  const antes: Paso[] = [
    {
      id: 'explicacion',
      titulo,
      explicacion: <p>{t('entrega.explicacion', { nombre })}</p>,
      seguir: { etiqueta: t('turno.aContar'), icono: 'contar', alTocar: () => siguiente() },
    },
    pasoDeConteo({
      id: 'cordobas',
      moneda: 'NIO',
      contando: cordobas,
      poner: setCordobas,
      esperado: esperadoC,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarCordobas'),
      explicacion: t('turno.contarCiego'),
      siguiente: () => siguiente(),
      t,
    }),
    pasoDeConteo({
      id: 'dolares',
      moneda: 'USD',
      contando: dolares,
      poner: setDolares,
      esperado: esperadoD,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarDolares'),
      explicacion: t('turno.contarDolaresCerrar'),
      siguiente: () => siguiente(),
      t,
    }),
    {
      id: 'confirmar',
      titulo: t('turno.revise'),
      explicacion: <p>{t('turno.reviseRecibir', { nombre })}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(cC ?? (0 as Centavos))} />
            <Dato etiqueta={t('turno.contoDolares')} valor={enMoneda(cD ?? (0 as Centavos), 'USD')} />
          </div>
          <Campo etiqueta={t('turno.notas')} value={nota} onChange={(e) => setNota(e.target.value)} autoComplete="off" maxLength={300} />
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: t('entrega.recibir'),
        icono: 'contar',
        deshabilitado: guardando || cC === null || cD === null,
        alTocar: () => void recibir(),
      },
    },
  ];

  const difC = recibida ? centavos(recibida.contadoC - recibida.esperadoC) : 0;
  const difD = recibida ? centavos(recibida.contadoD - recibida.esperadoD) : 0;
  const despues: Paso[] = [
    {
      id: 'resultado',
      titulo: t('entrega.recibida'),
      sinAtras: true,
      explicacion: (
        <p>{difC === 0 && difD === 0 ? t('entrega.aSuNombre') : t('entrega.diferenciaDe', { nombre })}</p>
      ),
      contenido: recibida && (
        <div className="flex flex-col gap-5">
          <Veredicto
            esperado={t('caja.esperadoCordobas')}
            valorEsperado={formatMoney(recibida.esperadoC)}
            valorContado={formatMoney(recibida.contadoC)}
            diferencia={difC as Centavos}
            cuadra={t('caja.cordobasCuadran')}
            formato={(c) => formatMoney(c)}
            primerConteo={recibida.primeroC !== null ? formatMoney(recibida.primeroC) : null}
            calculo={calculoDelEsperado(recibida.caja, 'NIO', t)}
          />
          {(recibida.esperadoD > 0 || recibida.contadoD > 0) && (
            <Veredicto
              esperado={t('caja.esperadoDolares')}
              valorEsperado={enMoneda(recibida.esperadoD, 'USD')}
              valorContado={enMoneda(recibida.contadoD, 'USD')}
              diferencia={difD as Centavos}
              cuadra={t('caja.dolaresCuadran')}
              formato={(c) => enMoneda(c, 'USD')}
              primerConteo={recibida.primeroD !== null ? enMoneda(recibida.primeroD, 'USD') : null}
              calculo={calculoDelEsperado(recibida.caja, 'USD', t)}
            />
          )}
          <Link to="/" replace onClick={atrasHasta('/')} className="btn-secundario">
            <Ico icono="inicio" />
            {t('turno.irAlInicio')}
          </Link>
        </div>
      ),
      // Back to the till that sent here, an order perhaps waiting in its address, or to it.
      seguir: {
        etiqueta: t('inicio.vender'),
        icono: 'vender',
        alTocar: () => void irA({ to: '/pos', ...(pedido ? { search: { pedido } } : {}) }),
      },
    },
  ];

  const pasos = recibida ? despues : antes;
  const ids = pasos.map((p) => p.id);
  const i = indicePaso(ids, actual, ultimo.current);
  ultimo.current = i;
  const paso = pasos[i]!;
  function siguiente() {
    setActual(ids[i + 1] ?? ids[i]!);
  }
  const todos = [...antes, ...despues].map((p) => p.id);

  return (
    <Asistente
      titulo={titulo}
      paso={paso}
      numero={todos.indexOf(paso.id) + 1}
      total={todos.length}
      volverA="/caja"
      {...(i > 0 ? { onAtras: () => setActual(ids[i - 1]!) } : {})}
    />
  );
}
